import { useEffect } from 'react';
import './ThunderFangAnimation.css';

/**
 * PROFESSIONAL THUNDER FANG ANIMATION
 * Electric-type physical move with electrified bite mechanics
 * 
 * Key Features:
 * - Electric-type physical attack (65 base power, 95% accuracy)
 * - 10% chance to cause paralysis, 10% chance to cause flinching
 * - Electrified bite with lightning fang effects
 * - Professional gaming company quality with predatory timing
 * - Physical move animation with electrical bite mechanics
 */
export default function ThunderFangAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    // Get battle container for positioning
    const container = playerEl.closest('.battle-screen') || playerEl.closest('.battle-container') || document.body;
    const containerRect = container.getBoundingClientRect();
    
    // Calculate positions
    const playerRect = playerEl.getBoundingClientRect();
    const opponentRect = opponentEl.getBoundingClientRect();
    
    const playerX = Math.round((playerRect.left + playerRect.width / 2) - containerRect.left);
    const playerY = Math.round((playerRect.top + playerRect.height / 2) - containerRect.top);
    const opponentX = Math.round((opponentRect.left + opponentRect.width / 2) - containerRect.left);
    const opponentY = Math.round((opponentRect.top + opponentRect.height / 2) - containerRect.top);

    // Helpers to keep elements inside container bounds
    const clampX = (x, margin = 0) => Math.min(Math.max(x, margin), Math.round(containerRect.width) - margin);
    const clampY = (y, margin = 0) => Math.min(Math.max(y, margin), Math.round(containerRect.height) - margin);

    // Calculate bite trajectory
    const dx = opponentX - playerX;
    const dy = opponentY - playerY;
    const distance = Math.hypot(dx, dy);
    const created = [];

    // Crunch-like bite overlay sequence (simplified, mechanics-accurate)
    const frameW = 156;
    const frameH = 146.5;
    opponentEl.classList.add('thunder-fang-opponent-hit');
    const bite = document.createElement('div');
    bite.className = 'thunder-fang-bite-anim';
    bite.style.setProperty('--sx', `${opponentX - frameW / 2}px`);
    bite.style.setProperty('--sy', `${opponentY - frameH / 2}px`);
    container.appendChild(bite);

    const sparkTimer = setTimeout(() => {
      const spark = document.createElement('div');
      spark.className = 'thunder-fang-spark';
      spark.style.left = `${opponentX}px`;
      spark.style.top = `${opponentY}px`;
      container.appendChild(spark);
      setTimeout(() => spark.remove(), 400);
      const ring = document.createElement('div');
      ring.className = 'thunder-fang-mini-ring';
      ring.style.left = `${opponentX - 70}px`;
      ring.style.top = `${opponentY - 70}px`;
      ring.style.width = '140px';
      ring.style.height = '140px';
      container.appendChild(ring);
      created.push(ring);
      for (let i = 0; i < 3; i++) {
        const a = (i / 3) * 360;
        const r = 55;
        const ax = opponentX + Math.cos(a * Math.PI / 180) * r;
        const ay = opponentY + Math.sin(a * Math.PI / 180) * r;
        const arc = document.createElement('div');
        arc.className = 'thunder-fang-arc';
        arc.style.left = `${ax - 6}px`;
        arc.style.top = `${ay - 6}px`;
        arc.style.setProperty('--tx', `${Math.cos(a * Math.PI / 180) * 20}px`);
        arc.style.setProperty('--ty', `${Math.sin(a * Math.PI / 180) * 20}px`);
        arc.textContent = '⚡';
        container.appendChild(arc);
        created.push(arc);
      }
      opponentEl.classList.add('thunder-fang-electric-flash');
      setTimeout(() => opponentEl.classList.remove('thunder-fang-electric-flash'), 160);
    }, 500);

    const doneTimer = setTimeout(() => {
      opponentEl.classList.remove('thunder-fang-opponent-hit');
      if (bite && bite.parentNode) bite.remove();
      created.forEach(el => { if (el && el.parentNode) el.remove(); });
      onComplete();
    }, 800);

    return () => {
      clearTimeout(sparkTimer);
      clearTimeout(doneTimer);
      opponentEl.classList.remove('thunder-fang-opponent-hit');
      if (bite && bite.parentNode) bite.remove();
      created.forEach(el => { if (el && el.parentNode) el.remove(); });
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

// Set move type for battle system
ThunderFangAnimation.moveType = 'electric';
